import React, { useMemo, useState } from 'react';
import FriendButton from '~/components/FriendButton';
import Popup from './Popup';
import Icon from '~/components/Icon';
import ProfilePic from '~/components/ProfilePic';
import RichText from '~/components/Texts/RichText';
import AchievementBadges from '~/components/AchievementBadges';
import ChessLevelBadge from '~/components/ChessLevelBadge';
import UserTitle from '~/components/Texts/UserTitle';
import Loading from '~/components/Loading';
import UsernameHistoryModal from '~/components/Modals/UsernameHistoryModal';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { addCommasToNumber } from '~/helpers/stringHelpers';
import { useAppContext, useChatContext, useKeyContext } from '~/contexts';
import { User } from '~/types';
import { getSectionFromPathname } from '~/helpers';
import { saveScrollAnchorForElement } from '~/helpers/hooks/useScrollAnchorRestoration';
import { borderRadius, Color, mobileMaxWidth } from '~/constants/css';
import { css } from '@emotion/css';
import RankBadge from '~/components/RankBadge';
import PlatinumSurface from '~/components/PlatinumSurface';
import { platinumCoverOf } from '~/constants/profileThemes';

const chatLabel = 'Chat';
const profileLabel = 'Profile';
const xpRankBadgeWrapperClass = css`
  display: inline-flex;
  align-items: center;
  margin-left: 0.5rem;
  gap: 0.25rem;
  font-weight: 600;
`;
const xpRankBadgeClass = css`
  font-size: 1.1rem;
  min-width: 2.1rem;
  height: 1.6rem;
`;

// Profile / Cards / Chat / Add friend. The popup is at most half the screen
// wide, so on a phone four side-by-side buttons spill out of it: there they sit
// two by two, icon over label.
const actionRowClass = css`
  padding: 1rem;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  @media (max-width: ${mobileMaxWidth}) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.4rem 0.2rem;
    padding: 0.6rem 0.3rem;
    > * {
      min-width: 0;
      flex-direction: column;
      text-align: center;
      padding: 0.4rem 0.2rem !important;
      gap: 0.3rem;
    }
    > * span {
      margin-left: 0 !important;
      font-size: 1.1rem !important;
      max-width: 100%;
      white-space: normal;
      overflow-wrap: anywhere;
    }
  }
`;

export default function UserPopup({
  isLoading,
  myId,
  onHide,
  onMouseEnter,
  onMouseLeave,
  onSetPopupContext,
  popupContext,
  triggerRef,
  user,
  wordMasterContext,
  wordMasterPoints,
  wordMasterLabel,
  activityContext,
  activityPoints
}: {
  isLoading?: boolean;
  bio?: string;
  isOnline?: boolean;
  myId: number;
  triggerRef?: React.RefObject<HTMLElement | null>;
  onHide: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onSetPopupContext: (context: any) => void;
  popupContext: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  user: User;
  wordMasterContext?: boolean;
  wordMasterPoints?: number;
  wordMasterLabel?: string;
  activityContext?: string;
  activityPoints?: number;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const usingChat = useMemo(
    () => getSectionFromPathname(location?.pathname)?.section === 'chat',
    [location?.pathname]
  );

  const authLevel = useAppContext(
    (v) => v.user.state.userObj[user.id]?.authLevel
  );
  const hasUsernameChanged = useAppContext(
    (v) => v.user.state.userObj[user.id]?.hasUsernameChanged
  );
  const level = useAppContext((v) => v.user.state.userObj[user.id]?.level);
  const rank = useAppContext((v) => v.user.state.userObj[user.id]?.rank);
  const twinkleXP = useAppContext(
    (v) => v.user.state.userObj[user.id]?.twinkleXP
  );
  const profileTheme = useAppContext(
    (v) => v.user.state.userObj[user.id]?.profileTheme
  );
  const realName = useAppContext(
    (v) => v.user.state.userObj[user.id]?.realName
  );
  const title = useAppContext((v) => v.user.state.userObj[user.id]?.title);
  const unlockedAchievementIds = useAppContext(
    (v) => v.user.state.userObj[user.id]?.unlockedAchievementIds
  );
  const userType = useAppContext(
    (v) => v.user.state.userObj[user.id]?.userType
  );
  const profileFirstRow = useAppContext(
    (v) => v.user.state.userObj[user.id]?.profileFirstRow
  );
  const chessMaxLevelUnlocked = useAppContext(
    (v) => v.user.state.userObj[user.id]?.chessMaxLevelUnlocked
  );
  const xpThisMonth = useAppContext(
    (v) => v.user.state.userObj[user.id]?.xpThisMonth
  );

  const userId = useKeyContext((v) => v.myState.userId);
  const username = useKeyContext((v) => v.myState.username);
  const profilePicUrl = useKeyContext((v) => v.myState.profilePicUrl);
  const onUpdateSelectedChannelId = useChatContext(
    (v) => v.actions.onUpdateSelectedChannelId
  );
  const onOpenNewChatTab = useChatContext((v) => v.actions.onOpenNewChatTab);
  const [usernameHistoryShown, setUsernameHistoryShown] = useState(false);
  const [titleModalShown, setTitleModalShown] = useState(false);

  const reportError = useAppContext((v) => v.requestHelpers.reportError);
  const chatStatus = useChatContext((v) => v.state.chatStatus);
  const {
    isOnline: onlineFromStatus,
    isAway,
    isBusy
  } = chatStatus[user.id] || {};
  const online =
    typeof onlineFromStatus === 'boolean'
      ? onlineFromStatus
      : typeof user.isOnline === 'boolean'
      ? user.isOnline
      : false;
  const loadDMChannel = useAppContext((v) => v.requestHelpers.loadDMChannel);
  const userRank = useMemo(() => {
    if (user.rank) {
      return Number(user.rank);
    }
    return rank;
  }, [rank, user.rank]);

  const appliedProfileTheme = useMemo(() => {
    return user.profileTheme || profileTheme;
  }, [user.profileTheme, profileTheme]);
  const platinum = platinumCoverOf({ ...user, profileTheme: appliedProfileTheme });
  const appliedRealName = useMemo(() => {
    return user.realName || realName;
  }, [realName, user.realName]);
  const appliedUnlockedAchievementIds = useMemo(() => {
    return user.unlockedAchievementIds || unlockedAchievementIds;
  }, [unlockedAchievementIds, user.unlockedAchievementIds]);
  const bio = useMemo(() => {
    return user.profileFirstRow || profileFirstRow;
  }, [user.profileFirstRow, profileFirstRow]);
  const userXP = useMemo(() => {
    if (!twinkleXP && !user.twinkleXP) {
      return null;
    }
    return addCommasToNumber(twinkleXP || user.twinkleXP);
  }, [twinkleXP, user.twinkleXP]);

  const userXPThisMonth = useMemo(() => {
    if (!user.xpThisMonth && !xpThisMonth) {
      return null;
    }
    return addCommasToNumber(xpThisMonth);
  }, [xpThisMonth, user.xpThisMonth]);
  const appliedChessMaxLevelUnlocked = useMemo(() => {
    return user.chessMaxLevelUnlocked ?? chessMaxLevelUnlocked;
  }, [chessMaxLevelUnlocked, user.chessMaxLevelUnlocked]);

  const xpContent = useMemo(() => {
    const renderBadge = (rank: number | undefined) => {
      if (!rank || rank >= 4) return null;

      return (
        <span className={xpRankBadgeWrapperClass}>
          <RankBadge rank={rank} className={xpRankBadgeClass} />
        </span>
      );
    };

    if (
      (wordMasterContext && typeof wordMasterPoints === 'number') ||
      (activityContext === 'wordMaster' && typeof activityPoints === 'number')
    ) {
      const points = wordMasterPoints || activityPoints || 0;
      const label = wordMasterLabel || (points === 1 ? 'pt' : 'pts');

      return (
        <>
          {addCommasToNumber(points)} {label}
          {renderBadge(userRank)}
        </>
      );
    }

    if (activityContext === 'monthlyXP') {
      return (
        <>
          {addCommasToNumber(user.twinkleXP || 0)} XP
          {renderBadge(userRank)}
        </>
      );
    }

    if (activityContext === 'wordleXP') {
      return (
        <>
          {addCommasToNumber(user.xpEarned || 0)} XP from Wordle
          {renderBadge(userRank)}
        </>
      );
    }

    if (activityContext && typeof activityPoints === 'number') {
      let contextLabel = '';
      if (activityContext === 'aiStories') contextLabel = 'from AI Stories';
      else if (activityContext === 'grammar') contextLabel = 'from Grammarbles';

      return (
        <>
          {addCommasToNumber(activityPoints)} XP {contextLabel}
          {renderBadge(userRank)}
        </>
      );
    }

    return (
      <>
        {userXP} XP
        {renderBadge(userRank)}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    wordMasterContext,
    wordMasterPoints,
    activityContext,
    activityPoints,
    userXP,
    userRank,
    wordMasterLabel,
    user.rank,
    user.twinkleXP
  ]);

  const shouldShowMonthlyXP = useMemo(() => {
    return (
      !wordMasterContext &&
      activityContext !== 'monthlyXP' &&
      activityContext !== 'wordMaster' &&
      activityContext !== 'wordleXP' &&
      activityContext !== 'aiStories' &&
      activityContext !== 'grammar' &&
      userXPThisMonth
    );
  }, [wordMasterContext, activityContext, userXPThisMonth]);

  return (
    <Popup
      popupContext={popupContext}
      triggerRef={triggerRef}
      onHideMenu={handleHide}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        width: '33rem',
        maxWidth: '50vw',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        opacity: usernameHistoryShown || titleModalShown ? 0 : 1,
        pointerEvents:
          usernameHistoryShown || titleModalShown ? 'none' : undefined
      }}
    >
      {isLoading ? (
        <Loading />
      ) : (
        <div
          style={{
            width: '100%',
            padding: '0',
            background: '#fff'
          }}
        >
          <div
            style={{
              position: 'relative',
              overflow: 'hidden',
              isolation: 'isolate',
              background: `radial-gradient(120% 140% at 20% 0%, rgba(255,255,255,0.18), rgba(255,255,255,0) 60%), ${(Color as any)[appliedProfileTheme || 'logoBlue']?.() || Color.logoBlue()}`,
              minHeight: platinum.photoUrl ? '5.5rem' : '2rem',
              padding: '0.5rem',
              display: 'flex',
              alignItems: 'flex-end'
            }}
          >
            {platinum.isPlatinum && (
              <PlatinumSurface
                size="strip"
                photoUrl={platinum.photoUrl}
                photoPosition={platinum.position}
                shine={platinum.shine}
                style={{ position: 'absolute', inset: 0, zIndex: 0 }}
              />
            )}
            <div style={{ position: 'relative', zIndex: 1, width: '100%' }}>
              <AchievementBadges
                thumbSize="2rem"
                unlockedAchievementIds={appliedUnlockedAchievementIds}
              />
            </div>
          </div>
          <div style={{ padding: '0.7rem 1rem 1rem 1rem' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '1rem'
              }}
            >
              <div
                className={css`
                  width: 5rem;
                  flex-shrink: 0;
                  @media (max-width: ${mobileMaxWidth}) {
                    width: 3rem;
                  }
                `}
              >
                <ProfilePic
                  style={{ width: '100%' }}
                  profilePicUrl={user.profilePicUrl || ''}
                  userId={user.id}
                  online={online}
                  isAway={isAway}
                  isBusy={isBusy}
                  statusShown
                />
              </div>
              <div
                style={{
                  marginLeft: '1rem',
                  minWidth: 0,
                  overflowWrap: 'anywhere'
                }}
              >
                <div
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: '#333'
                  }}
                >
                  <span
                    className={`unselectable ${css`
                      cursor: ${hasUsernameChanged ? 'pointer' : 'default'};
                      &:hover {
                        text-decoration: ${hasUsernameChanged
                          ? 'underline'
                          : 'none'};
                      }
                    `}`}
                    onClick={() =>
                      hasUsernameChanged && setUsernameHistoryShown(true)
                    }
                  >
                    {user.username}
                  </span>
                </div>
                <div style={{ fontSize: '1.1rem' }}>
                  <UserTitle
                    user={{
                      ...user,
                      level: user.level || level,
                      title: user.title || title,
                      userType: user.userType || userType,
                      authLevel: user.authLevel || authLevel
                    }}
                    style={{ fontSize: '1.1rem' }}
                    className={`unselectable ${css`
                      font-weight: bold;
                      display: inline;
                      margin-right: 0.3rem;
                      color: ${Color.darkGray()};
                    `}`}
                    onTitleModalShown={setTitleModalShown}
                  />
                  {appliedRealName}
                </div>
                <ChessLevelBadge
                  level={appliedChessMaxLevelUnlocked}
                  size="sm"
                  user={user}
                  onOpenOptions={() => onSetPopupContext(null)}
                  style={{ marginTop: '0.45rem' }}
                />
              </div>
            </div>
            {bio && (
              <RichText
                isProfileComponent
                theme={profileTheme}
                style={{
                  marginBottom: '1rem',
                  color: '#333',
                  fontSize: '1.1rem'
                }}
              >
                {bio}
              </RichText>
            )}
            <div className={actionRowClass}>
              <Link
                style={{
                  color: Color.darkerGray(),
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.5rem',
                  borderRadius,
                  transition: 'background 0.3s',
                  flexGrow: 1,
                  justifyContent: 'center'
                }}
                className={css`
                  &:hover {
                    text-decoration: none;
                  }
                `}
                to={`/users/${user.username}`}
                onClick={handleSaveScrollAnchor}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = Color.highlightGray())
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = 'transparent')
                }
              >
                <Icon icon="user" />
                <span style={{ marginLeft: '1rem', fontSize: '1.2rem' }}>
                  {profileLabel}
                </span>
              </Link>
              <Link
                style={{
                  color: Color.darkerGray(),
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.5rem',
                  borderRadius,
                  transition: 'background 0.3s',
                  flexGrow: 1,
                  justifyContent: 'center'
                }}
                className={css`
                  &:hover {
                    text-decoration: none;
                  }
                `}
                to={`/ai-cards/?search[owner]=${user.username}`}
                onClick={handleSaveScrollAnchor}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = Color.highlightGray())
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = 'transparent')
                }
              >
                <Icon icon="cards-blank" />
                <span style={{ marginLeft: '1rem', fontSize: '1.2rem' }}>
                  Cards
                </span>
              </Link>
              {user.id !== myId && (
                <div
                  style={{
                    color: Color.darkerGray(),
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.5rem',
                    borderRadius,
                    transition: 'background 0.3s',
                    flexGrow: 1,
                    justifyContent: 'center'
                  }}
                  onClick={handleLinkClick}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = Color.highlightGray())
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = 'transparent')
                  }
                >
                  <Icon icon="comment" />
                  <span style={{ marginLeft: '1rem', fontSize: '1.2rem' }}>
                    {chatLabel}
                  </span>
                </div>
              )}
              {user.id !== myId && <FriendButton userId={user.id} variant="popup" />}
            </div>
            {userXP && (
              <div
                style={{
                  padding: '0.5rem 1rem',
                  background:
                    !!userRank && userRank < 4
                      ? Color.darkerGray()
                      : Color.highlightGray(),
                  color:
                    !!userRank && userRank < 4 ? '#fff' : Color.darkerGray(),
                  borderRadius,
                  textAlign: 'center',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: '1.1rem',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  flexDirection: 'column'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%'
                  }}
                >
                  {xpContent}
                </div>

                {shouldShowMonthlyXP && (
                  <div
                    style={{
                      fontSize: '1.1rem',
                      fontWeight: 'normal'
                    }}
                  >
                    (↑ {userXPThisMonth} this month)
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {usernameHistoryShown ? (
        <UsernameHistoryModal
          userId={user.id}
          onHide={() => setUsernameHistoryShown(false)}
        />
      ) : null}
    </Popup>
  );

  function handleHide() {
    if (!(usernameHistoryShown || titleModalShown)) {
      onHide();
    }
  }

  function handleSaveScrollAnchor() {
    saveScrollAnchorForElement(triggerRef?.current || null);
  }

  async function handleLinkClick() {
    handleSaveScrollAnchor();
    onSetPopupContext(null);

    if (!userId) {
      return;
    }

    if (user.id === userId) {
      return;
    }

    const { channelId, pathId } = await loadDMChannel({ recipient: user });
    if (!pathId) {
      if (!user?.id) {
        return reportError({
          componentPath: 'components/UserPopup/index',
          message: `handleLinkClick: recipient userId is null. recipient: ${JSON.stringify(
            user
          )}`
        });
      }
      onOpenNewChatTab({
        user: { username, id: userId, profilePicUrl },
        recipient: {
          username: user.username,
          id: user.id,
          profilePicUrl: user.profilePicUrl
        }
      });
      if (!usingChat) {
        onUpdateSelectedChannelId(channelId);
      }
    }
    setTimeout(() => navigate(pathId ? `/chat/${pathId}` : `/chat/new`), 0);
  }
}
